import { useCallback, useEffect, useState } from "react";

export default function HomeViewer({ views = [] }) {
    const [index, setIndex] = useState(0);
    const [mode, setMode] = useState("day");
    const [animating, setAnimating] = useState(false);
    const [loaded, setLoaded] = useState(false);

    /*
     * =========================================================
     * PRELOAD IMAGE
     * =========================================================
     */

    const preloadImage = useCallback((src) => {
        if (!src) return;

        const img = new Image();

        img.decoding = "async";
        img.src = src;

        if (img.decode) {
            img.decode().catch(() => { });
        }
    }, []);

    /*
     * =========================================================
     * LOAD CURRENT IMAGE
     * =========================================================
     */

    useEffect(() => {
        if (!views.length) return;

        const currentImage =
            mode === "day"
                ? views[index]?.day
                : views[index]?.night;

        if (!currentImage) {
            setLoaded(true);
            return;
        }

        setLoaded(false);

        const img = new Image();

        img.decoding = "async";

        img.onload = async () => {
            try {
                if (img.decode) {
                    await img.decode();
                }
            } catch {
                // Ignore decode errors
            }

            setLoaded(true);
        };

        img.onerror = () => {
            setLoaded(true);
        };

        img.src = currentImage;

        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [index, mode, views]);

    /*
     * =========================================================
     * PRELOAD NEARBY IMAGES
     * =========================================================
     */

    useEffect(() => {
        if (!views.length) return;

        const timer = setTimeout(() => {
            const nextIndex =
                (index + 1) % views.length;

            const previousIndex =
                index === 0
                    ? views.length - 1
                    : index - 1;

            const nearbyViews = [
                views[nextIndex],
                views[previousIndex],
            ];

            nearbyViews.forEach((view) => {
                if (!view) return;

                if (view.day) {
                    preloadImage(view.day);
                }

                if (view.night) {
                    preloadImage(view.night);
                }
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [index, views, preloadImage]);

    /*
     * =========================================================
     * TRANSITION
     * =========================================================
     */

    const transition = async (nextIndex) => {
        if (animating) return;

        setAnimating(true);

        const img =
            document.getElementById("cinema-img");

        if (img) {
            img.style.transform = "scale(1.025)";
            img.style.opacity = "0";
        }

        await new Promise((resolve) =>
            setTimeout(resolve, 250)
        );

        setIndex(nextIndex);

        setTimeout(() => {
            const newImg =
                document.getElementById("cinema-img");

            if (newImg) {
                newImg.style.transform = "scale(1)";
                newImg.style.opacity = "1";
            }

            setAnimating(false);
        }, 50);
    };

    /*
     * =========================================================
     * NEXT
     * =========================================================
     */

    const next = () => {
        if (!views.length || animating) return;

        transition(
            (index + 1) % views.length
        );
    };

    /*
     * =========================================================
     * PREVIOUS
     * =========================================================
     */

    const prev = () => {
        if (!views.length || animating) return;

        transition(
            index === 0
                ? views.length - 1
                : index - 1
        );
    };

    /*
     * =========================================================
     * DAY / NIGHT
     * =========================================================
     */

    const toggleMode = async () => {
        if (animating) return;

        const nextMode =
            mode === "day"
                ? "night"
                : "day";

        const nextImage =
            nextMode === "day"
                ? views[index]?.day
                : views[index]?.night;

        if (!nextImage) {
            setMode(nextMode);
            return;
        }

        setAnimating(true);

        /*
         * Preload target image first
         */
        const img = new Image();

        img.decoding = "async";
        img.src = nextImage;

        try {
            if (img.decode) {
                await img.decode();
            }
        } catch {
            // Ignore decode errors
        }

        const currentImg =
            document.getElementById("cinema-img");

        if (currentImg) {
            currentImg.style.opacity = "0";
            currentImg.style.transform =
                "scale(1.025)";
        }

        await new Promise((resolve) =>
            setTimeout(resolve, 250)
        );

        setMode(nextMode);

        setTimeout(() => {
            const newImg =
                document.getElementById("cinema-img");

            if (newImg) {
                newImg.style.opacity = "1";
                newImg.style.transform =
                    "scale(1)";
            }

            setAnimating(false);
        }, 50);
    };

    /*
     * =========================================================
     * EMPTY
     * =========================================================
     */

    if (!views.length) {
        return null;
    }

    const currentImage =
        mode === "day"
            ? views[index]?.day
            : views[index]?.night;

    /*
     * =========================================================
     * LOADER
     * =========================================================
     */

    if (!loaded) {
        return (
            <div style={styles.loadingScreen}>
                <div style={styles.loaderGlow}>
                    <div style={styles.loader} />
                </div>

                <div style={styles.loadingBrand}>
                    KESAR
                    <span style={{ color: "#60a5fa", marginLeft: 6 }}>ELEGANCE</span>
                </div>

                <div style={styles.loadingText}>
                    Preparing your experience
                </div>

                <style>
                    {`
                        @keyframes spin {
                            from {
                                transform: rotate(0deg);
                            }

                            to {
                                transform: rotate(360deg);
                            }
                        }

                        @keyframes pulseGlow {
                            0%, 100% {
                                opacity: 0.5;
                                transform: scale(0.95);
                            }

                            50% {
                                opacity: 1;
                                transform: scale(1.05);
                            }
                        }

                        .home-nav:hover {
                            background: rgba(37, 99, 235, 0.72) !important;
                            border-color: rgba(96, 165, 250, 0.8) !important;
                            box-shadow: 0 12px 35px rgba(37, 99, 235, 0.35) !important;
                        }

                        .home-small-btn:hover {
                            background: rgba(37, 99, 235, 0.7) !important;
                            border-color: rgba(96, 165, 250, 0.8) !important;
                        }

                        .home-mode:hover {
                            border-color: rgba(96, 165, 250, 0.65) !important;
                            background: rgba(15, 35, 70, 0.85) !important;
                        }
                    `}
                </style>
            </div>
        );
    }

    /*
     * =========================================================
     * VIEWER
     * =========================================================
     */

    return (
        <div style={styles.viewer}>

            {/* ================================================= */}
            {/* BACKGROUND */}
            {/* ================================================= */}

            <div style={styles.backgroundGlowOne} />
            <div style={styles.backgroundGlowTwo} />

            {/* ================================================= */}
            {/* IMAGE */}
            {/* ================================================= */}

            <img
                id="cinema-img"
                src={currentImage}
                alt={`Kesar Elegance ${index + 1}`}
                draggable={false}
                decoding="async"
                fetchPriority="high"
                style={styles.image}
            />


            {/* ================================================= */}
            {/* DAY / NIGHT */}
            {/* ================================================= */}

            <button
                type="button"
                onClick={toggleMode}
                disabled={animating}
                className="home-mode"
                style={styles.modeButton}
            >
                <div
                    style={{
                        ...styles.modeIcon,
                        background:
                            mode === "day"
                                ? "linear-gradient(135deg, #2563eb, #60a5fa)"
                                : "linear-gradient(135deg, #1e3a8a, #3b82f6)",
                    }}
                >
                    {mode === "day" ? "☀" : "☾"}
                </div>

                <div style={styles.modeInfo}>
                    <span style={styles.modeLabel}>
                        MODE
                    </span>

                    <span style={styles.modeValue}>
                        {mode === "day"
                            ? "Day"
                            : "Night"}
                    </span>
                </div>

                <div style={styles.modeArrow}>
                    ↕
                </div>
            </button>


            {/* ================================================= */}
            {/* BOTTOM CONTROLS */}
            {/* ================================================= */}

            <div style={styles.bottomWrapper}>

                <div style={styles.bottomBar}>

                    <button
                        type="button"
                        onClick={prev}
                        disabled={animating}
                        className="home-small-btn"
                        style={styles.smallButton}
                    >
                        ←
                    </button>

                    <div style={styles.progressArea}>

                        <div style={styles.progressText}>
                            <span>
                                {String(index + 1).padStart(2, "0")}
                            </span>

                            <span style={styles.progressSlash}>
                                /
                            </span>

                            <span>
                                {String(views.length).padStart(2, "0")}
                            </span>
                        </div>

                        <div style={styles.progressTrack}>
                            <div
                                style={{
                                    ...styles.progressFill,
                                    width: `${((index + 1) / views.length) * 100}%`,
                                }}
                            />
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={next}
                        disabled={animating}
                        className="home-small-btn"
                        style={styles.smallButton}
                    >
                        →
                    </button>

                </div>

                <div style={styles.experienceText}>
                    EXPLORE KESAR ELEGANCE
                </div>

            </div>

            {/* ================================================= */}
            {/* SIDE LABEL */}
            {/* ================================================= */}

            <div style={styles.sideLabel}>
                <span style={styles.sideLine} />
                <span>
                    {mode === "day"
                        ? "DAY VIEW"
                        : "NIGHT VIEW"}
                </span>
            </div>

            {/* ================================================= */}
            {/* CSS */}
            {/* ================================================= */}

            <style>
                {`
                    @keyframes spin {
                        from {
                            transform: rotate(0deg);
                        }

                        to {
                            transform: rotate(360deg);
                        }
                    }

                    @keyframes pulseGlow {
                        0%, 100% {
                            opacity: 0.5;
                            transform: scale(0.95);
                        }

                        50% {
                            opacity: 1;
                            transform: scale(1.05);
                        }
                    }

                    .home-nav:hover {
                        background: rgba(37, 99, 235, 0.72) !important;
                        border-color: rgba(96, 165, 250, 0.8) !important;
                        box-shadow: 0 12px 35px rgba(37, 99, 235, 0.35) !important;
                    }

                    .home-small-btn:hover {
                        background: rgba(37, 99, 235, 0.7) !important;
                        border-color: rgba(96, 165, 250, 0.8) !important;
                    }

                    .home-mode:hover {
                        border-color: rgba(96, 165, 250, 0.65) !important;
                        background: rgba(15, 35, 70, 0.85) !important;
                    }

                    @media (max-width: 600px) {
                        .home-nav {
                            width: 44px !important;
                            height: 44px !important;
                            font-size: 30px !important;
                        }
                    }
                `}
            </style>

        </div>
    );
}


/* ========================================================= */
/* STYLES */
/* ========================================================= */

const styles = {

    /* ------------------------------------------------------- */
    /* VIEWER */
    /* ------------------------------------------------------- */

    viewer: {
        width: "100%",
        height: "100dvh",
        minHeight: 500,

        position: "relative",

        overflow: "hidden",

        background:
            "linear-gradient(135deg, #020617 0%, #06142f 45%, #020617 100%)",

        color: "#fff",

        userSelect: "none",

        fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    },

    /* ------------------------------------------------------- */
    /* BACKGROUND */
    /* ------------------------------------------------------- */

    backgroundGlowOne: {
        position: "absolute",

        width: 500,
        height: 500,

        borderRadius: "50%",

        background:
            "rgba(37, 99, 235, 0.12)",

        filter: "blur(120px)",

        top: "-250px",
        left: "-180px",

        pointerEvents: "none",

        zIndex: 1,
    },

    backgroundGlowTwo: {
        position: "absolute",

        width: 400,
        height: 400,

        borderRadius: "50%",

        background:
            "rgba(59, 130, 246, 0.10)",

        filter: "blur(110px)",

        bottom: "-200px",
        right: "-150px",

        pointerEvents: "none",

        zIndex: 1,
    },

    /* ------------------------------------------------------- */
    /* IMAGE */
    /* ------------------------------------------------------- */

    image: {
        position: "absolute",

        inset: 0,

        width: "100%",
        height: "100%",

        objectFit: "contain",

        transition:
            "opacity 0.45s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)",

        transform: "scale(1)",

        opacity: 1,

        userSelect: "none",

        WebkitUserDrag: "none",

        zIndex: 2,
    },

    /* ------------------------------------------------------- */
    /* TOP GRADIENT */
    /* ------------------------------------------------------- */

    topGradient: {
        position: "absolute",

        top: 0,
        left: 0,
        right: 0,

        height: 150,

        background:
            "linear-gradient(180deg, rgba(2,6,23,0.75) 0%, rgba(2,6,23,0.25) 55%, transparent 100%)",

        pointerEvents: "none",

        zIndex: 5,
    },

    /* ------------------------------------------------------- */
    /* HEADER */
    /* ------------------------------------------------------- */

    header: {
        position: "absolute",

        top: 22,
        left: 25,
        right: 25,

        display: "flex",

        justifyContent: "space-between",

        alignItems: "center",

        zIndex: 20,

        pointerEvents: "none",
    },

    brand: {
        display: "flex",

        alignItems: "center",

        gap: 11,
    },

    brandMark: {
        width: 38,
        height: 38,

        display: "flex",

        alignItems: "center",
        justifyContent: "center",

        borderRadius: 12,

        background:
            "linear-gradient(135deg, #2563eb, #60a5fa)",

        color: "#fff",

        fontSize: 18,

        fontWeight: 700,

        boxShadow:
            "0 8px 25px rgba(37,99,235,0.35)",
    },

    brandTitle: {
        fontSize: 13,

        fontWeight: 800,

        letterSpacing: "3px",

        lineHeight: 1,
    },

    brandSubtitle: {
        marginTop: 5,

        fontSize: 8,

        letterSpacing: "4px",

        color: "#60a5fa",

        fontWeight: 600,
    },

    /* ------------------------------------------------------- */
    /* COUNTER */
    /* ------------------------------------------------------- */

    counter: {
        display: "flex",

        alignItems: "center",

        gap: 8,

        padding: "9px 14px",

        borderRadius: 20,

        background:
            "rgba(2,6,23,0.58)",

        border:
            "1px solid rgba(96,165,250,0.18)",

        backdropFilter: "blur(14px)",

        color:
            "rgba(255,255,255,0.55)",

        fontSize: 12,

        fontWeight: 600,

        letterSpacing: "1px",
    },

    counterCurrent: {
        color: "#60a5fa",

        fontSize: 15,

        fontWeight: 700,
    },

    counterLine: {
        color:
            "rgba(255,255,255,0.2)",
    },

    /* ------------------------------------------------------- */
    /* MODE BUTTON */
    /* ------------------------------------------------------- */

    modeButton: {
        position: "absolute",

        top: 15,
        right: 100,

        display: "flex",

        alignItems: "center",

        gap: 10,

        padding: "6px 10px 6px 6px",

        borderRadius: 18,

        border:
            "1px solid rgba(96,165,250,0.2)",

        background:
            "rgba(2,6,23,0.62)",

        backdropFilter: "blur(14px)",

        color: "#fff",

        cursor: "pointer",

        transition: "all 0.25s ease",

        zIndex: 25,
    },

    modeIcon: {
        width: 34,
        height: 34,

        display: "flex",

        alignItems: "center",
        justifyContent: "center",

        borderRadius: 12,

        color: "#fff",

        fontSize: 16,

        boxShadow:
            "0 5px 18px rgba(37,99,235,0.35)",
    },

    modeInfo: {
        display: "flex",

        flexDirection: "column",

        alignItems: "flex-start",

        gap: 2,
    },

    modeLabel: {
        fontSize: 7,

        letterSpacing: "2px",

        color:
            "rgba(255,255,255,0.4)",

        fontWeight: 700,
    },

    modeValue: {
        fontSize: 12,

        fontWeight: 600,
    },

    modeArrow: {
        marginLeft: 3,

        color:
            "rgba(255,255,255,0.35)",

        fontSize: 13,
    },

    /* ------------------------------------------------------- */
    /* NAV BUTTONS */
    /* ------------------------------------------------------- */

    navButton: {
        position: "absolute",

        top: "50%",

        transform: "translateY(-50%)",

        width: 56,
        height: 56,

        display: "flex",

        alignItems: "center",
        justifyContent: "center",

        borderRadius: "50%",

        border:
            "1px solid rgba(147,197,253,0.22)",

        background:
            "rgba(2,6,23,0.55)",

        backdropFilter: "blur(14px)",

        color: "#fff",

        fontSize: 38,

        fontWeight: 200,

        lineHeight: 1,

        cursor: "pointer",

        transition:
            "all 0.25s ease",

        boxShadow:
            "0 8px 30px rgba(0,0,0,0.35)",

        zIndex: 20,
    },

    /* ------------------------------------------------------- */
    /* BOTTOM */
    /* ------------------------------------------------------- */

    bottomWrapper: {
        position: "absolute",

        bottom: 25,

        left: "50%",

        transform: "translateX(-50%)",

        display: "flex",

        flexDirection: "column",

        alignItems: "center",

        gap: 8,

        zIndex: 25,
    },

    bottomBar: {
        display: "flex",

        alignItems: "center",

        gap: 13,

        padding: "8px 10px",

        borderRadius: 22,

        background:
            "rgba(2,6,23,0.72)",

        border:
            "1px solid rgba(96,165,250,0.16)",

        backdropFilter: "blur(18px)",

        boxShadow:
            "0 15px 40px rgba(0,0,0,0.4)",
    },

    smallButton: {
        width: 38,
        height: 38,

        display: "flex",

        alignItems: "center",
        justifyContent: "center",

        borderRadius: "50%",

        border:
            "1px solid rgba(147,197,253,0.18)",

        background:
            "rgba(255,255,255,0.06)",

        color: "#fff",

        fontSize: 17,

        cursor: "pointer",

        transition: "all 0.2s ease",
    },

    progressArea: {
        width: 100,

        display: "flex",

        flexDirection: "column",

        gap: 6,
    },

    progressText: {
        display: "flex",

        justifyContent: "center",

        alignItems: "center",

        gap: 5,

        fontSize: 10,

        fontWeight: 600,

        color: "#60a5fa",

        letterSpacing: "1px",
    },

    progressSlash: {
        color:
            "rgba(255,255,255,0.25)",
    },

    progressTrack: {
        width: "100%",

        height: 2,

        borderRadius: 10,

        background:
            "rgba(255,255,255,0.12)",

        overflow: "hidden",
    },

    progressFill: {
        height: "100%",

        borderRadius: 10,

        background:
            "linear-gradient(90deg, #2563eb, #60a5fa)",

        transition:
            "width 0.4s ease",
    },

    experienceText: {
        fontSize: 7,

        letterSpacing: "3px",

        color:
            "rgba(255,255,255,0.35)",

        whiteSpace: "nowrap",
    },

    /* ------------------------------------------------------- */
    /* SIDE LABEL */
    /* ------------------------------------------------------- */

    sideLabel: {
        position: "absolute",

        left: 25,

        top: "50%",

        transform:
            "translateY(-50%) rotate(-90deg)",

        transformOrigin: "left center",

        display: "flex",

        alignItems: "center",

        gap: 10,

        color:
            "rgba(255,255,255,0.35)",

        fontSize: 8,

        letterSpacing: "3px",

        fontWeight: 700,

        zIndex: 15,

        pointerEvents: "none",
    },

    sideLine: {
        width: 30,

        height: 1,

        background:
            "linear-gradient(90deg, transparent, #60a5fa)",
    },

    /* ------------------------------------------------------- */
    /* LOADING */
    /* ------------------------------------------------------- */

    loadingScreen: {
        width: "100%",
        height: "100dvh",

        display: "flex",

        flexDirection: "column",

        alignItems: "center",
        justifyContent: "center",

        gap: 14,

        background:
            "radial-gradient(circle at center, #0b1f46 0%, #020617 45%, #00030a 100%)",

        color: "#fff",
    },

    loaderGlow: {
        width: 75,
        height: 75,

        display: "flex",

        alignItems: "center",
        justifyContent: "center",

        borderRadius: "50%",

        background:
            "rgba(37,99,235,0.12)",

        boxShadow:
            "0 0 50px rgba(37,99,235,0.25)",

        animation:
            "pulseGlow 2s ease-in-out infinite",
    },

    loader: {
        width: 42,
        height: 42,

        borderRadius: "50%",

        border:
            "3px solid rgba(96,165,250,0.15)",

        borderTop:
            "3px solid #60a5fa",

        borderRight:
            "3px solid #2563eb",

        animation:
            "spin 0.8s linear infinite",
    },

    loadingBrand: {
        fontSize: 15,

        fontWeight: 800,

        letterSpacing: "4px",
    },

    loadingText: {
        fontSize: 11,

        color:
            "rgba(255,255,255,0.4)",

        letterSpacing: "1px",
    },
};